import type { OnboardingStepProps } from "./Onboarding";

import React, { useCallback } from "react";

import { useCurrentWorkspace } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useIsWorkspaceOwner } from "@editor/hooks/useIsWorkspaceOwner";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import Switch from "@replo/design-system/components/switch/Switch";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BillingTiers,
  getBillingPlanInfoByTier,
  getNonCustomBillingPlans,
} from "schemas/billing";

import PlanCard from "../../../features/workspace/components/PlanCard";
import OnboardingFooter from "./OnboardingFooter";

const ChoosePlan: React.FC<OnboardingStepProps> = ({
  currentStep,
  submitStep,
  isLoading,
  totalSteps,
}) => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;

  const [isYearly, setIsYearly] = React.useState(true);

  const isWorkspaceOwner = useIsWorkspaceOwner(workspaceId);

  const nonCustomBillingPlans = getNonCustomBillingPlans();
  const filteredPlans = nonCustomBillingPlans.filter(
    (plan) => plan.tier !== BillingTiers.FREE,
  );

  const handleSubmit = useCallback(() => {
    if (user) {
      logEvent("onboarding.flow.step.completed", {
        userId: user.email,
        stepId: "choose-plan",
        stepType: "choose-plan",
      });
    }
    submitStep();
  }, [user, logEvent, submitStep]);

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(`/onboarding/workspace-details`);
  };

  const handleBillingIntervalChange = () => {
    setIsYearly((prev) => !prev);
  };

  return (
    <div className="flex h-screen w-full flex-col items-center overflow-y-auto">
      <div className="flex w-full flex-grow flex-col items-center overflow-y-auto md:py-12">
        <div className="flex w-full h-full flex-col items-center justify-between">
          <div className="pt-14 md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-10 md:gap-3">
            <h1 className="header-h1 text-3xl md:text-4xl">Choose your plan</h1>
          </div>

          <div className="w-full px-4 md:px-8 lg:px-12 max-w-[1200px]">
            <div className="flex justify-between mb-6">
              <a
                href="https://support.replo.app/articles/2238970734-what-replo-plan-is-right-for-me"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm font-medium hover:underline"
              >
                Read More About Plans <ArrowUpRight className="h-4 w-4" />
              </a>
              <div className="flex items-center gap-4">
                <span
                  className={twMerge(
                    "text-sm font-medium",
                    !isYearly ? "text-default" : "text-muted",
                  )}
                >
                  Pay Monthly
                </span>
                <Switch
                  size="base"
                  isOn={isYearly}
                  onChange={handleBillingIntervalChange}
                />
                <span
                  className={twMerge(
                    "text-sm font-medium",
                    isYearly ? "text-default" : "text-muted",
                  )}
                >
                  Pay annually (17% discount)
                </span>
              </div>
            </div>

            <div className="grid gap-4 mx-auto w-full mb-10 grid-cols-4">
              {filteredPlans.map((plan) => (
                <PlanCard
                  key={plan.tier}
                  plan={plan}
                  currentPlan="free"
                  isYearly={isYearly}
                  isWorkspaceOwner={isWorkspaceOwner}
                  workspace={workspace ?? undefined}
                  source="onboarding"
                />
              ))}
              <PlanCard
                plan={getBillingPlanInfoByTier("custom")}
                currentPlan="free"
                isYearly={isYearly}
                isWorkspaceOwner={isWorkspaceOwner}
                workspace={workspace ?? undefined}
                source="onboarding"
              />
            </div>
          </div>

          <OnboardingFooter
            currentStep={currentStep}
            onNext={handleSubmit}
            onBack={handleBack}
            className="w-full md:max-w-[1100px]"
            nextButtonText="Try for free"
            isNextLoading={isLoading}
            isNextDisabled={isLoading}
            totalSteps={totalSteps}
          />
        </div>
      </div>
    </div>
  );
};

export default ChoosePlan;
